import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useApprovalMfa from '../../../hooks/useApprovalMfa';
import { useAppNotifications, parseApiError } from '../../../utils/notify';

// ─────────────────────────────────────────────────────────────────
// Glass-i — useErpMaster
//
// Controller hook for ERP master pages. Wraps the bits every master
// repeats: row state, refresh / submit / delete / bulk-delete
// handlers, Approval-MFA step-up, and user notifications. Returns
// a `gridHandlers` object you spread onto <ErpDataGrid />, plus the
// <ApprovalMfaModalSlot/> JSX to mount once at page root.
//
// Two operating modes, picked per-handler:
//   1. Local-state mode (default for sandbox / demo) — handlers
//      mutate the in-memory `rows` array. Use this when the master
//      isn't yet wired to a real API.
//   2. Adapter mode — pass `adapter.{list,create,update,delete,bulkDelete}`
//      to delegate the actual write to a real API. The hook still
//      keeps a local copy of `rows` for the grid; on success it
//      optimistically updates that copy. On error it surfaces the
//      friendly error via notifyError and leaves the rows untouched.
//
// Approval MFA is independent of mode. If `approval.delete` (or
// `approval.bulkDelete`) is provided, the handler runs through
// useApprovalMfa first — backend creates a challenge, the user
// enters their TOTP code, and the protected action runs only after
// L1 (or L1 + L2) is verified.
//
// Identity helpers:
//   - getId(record)            → primary key for upsert / remove
//   - getResourceLabel(record) → human label shown in the approval modal
//   - getDisplayName(record)   → used in notification copy
//
// Customise notification copy via `notifyCopy`. Each entry returns
// `[title, description]`.
// ─────────────────────────────────────────────────────────────────

function defaultGetId(record) {
  return record && (record.id ?? record.key ?? null);
}

function pluralise(label, count) {
  if (count === 1) return label;
  return /s$/i.test(label) ? label : `${label}s`;
}

function makeLocalId() {
  return `tmp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

// Friendly error description, never leaks raw error.
function describeError(err, fallback) {
  if (err && err.name === 'ApprovalMfaCancelled') return null;
  const parsed = parseApiError(err);
  return (parsed && parsed.description) || fallback;
}

export default function useErpMaster(config = {}) {
  const {
    entityLabel: entityLabelInput = { singular: 'Record', plural: 'Records' },
    initialRows = [],
    getId = defaultGetId,
    getResourceLabel,
    getDisplayName,
    adapter,
    approval,
    resourceType,
    notifyCopy: notifyCopyInput,
    // Stage 6A — surfaced so callers can use a single config call to
    // wire the grid (persistence + bulk-action MFA + adapter + approval).
    // `gridId` is forwarded onto <ErpDataGrid /> via `gridHandlers`;
    // `bulkActions` is the declarative list (with permission/MFA opts)
    // that the grid's bulk-action bar consumes alongside the built-in
    // bulk-delete affordance.
    gridId,
    bulkActions: bulkActionsInput
  } = config;

  // Normalise entity label.
  const entityLabel = useMemo(() => {
    if (typeof entityLabelInput === 'string') {
      return {
        singular: entityLabelInput,
        plural: pluralise(entityLabelInput, 2)
      };
    }
    return {
      singular: entityLabelInput.singular || 'Record',
      plural:
        entityLabelInput.plural || pluralise(entityLabelInput.singular || 'Record', 2)
    };
  }, [entityLabelInput]);

  // Default notification copy — overridable via notifyCopy.
  const notifyCopy = useMemo(() => {
    const singular = entityLabel.singular;
    const defaults = {
      addSuccess: (r) => [
        `${singular} added`,
        getDisplayName && r ? `${getDisplayName(r)} created.` : 'Record created.'
      ],
      editSuccess: (r) => [
        `${singular} saved`,
        getDisplayName && r ? `${getDisplayName(r)} updated.` : 'Record updated.'
      ],
      deleteSuccess: (r) => [
        `${singular} deleted`,
        getDisplayName && r ? `${getDisplayName(r)} removed.` : 'Record removed.'
      ],
      bulkDeleteSuccess: (records) => [
        `${entityLabel.plural} deleted`,
        `${records.length} record${records.length === 1 ? '' : 's'} removed.`
      ],
      refreshSuccess: () => ['Refreshed', 'Latest data loaded.'],
      addError: () => [`Could not add ${singular.toLowerCase()}`, 'Please try again.'],
      editError: () => [`Could not save ${singular.toLowerCase()}`, 'Please try again.'],
      deleteError: () => [
        `Could not delete ${singular.toLowerCase()}`,
        'Please try again.'
      ],
      bulkDeleteError: () => [
        `Could not delete ${entityLabel.plural.toLowerCase()}`,
        'Please try again.'
      ],
      refreshError: () => ['Refresh failed', 'Please try again.']
    };
    return { ...defaults, ...(notifyCopyInput || {}) };
  }, [entityLabel, notifyCopyInput, getDisplayName]);

  const { notifySuccess, notifyError } = useAppNotifications();
  const { runWithApproval, ApprovalMfaModalSlot } = useApprovalMfa();

  const [rows, setRows] = useState(() =>
    typeof initialRows === 'function' ? initialRows() : initialRows
  );
  const [loading, setLoading] = useState(false);

  // Track the latest setRows so async handlers don't capture stale refs.
  const rowsRef = useRef(rows);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  // ── Refresh ───────────────────────────────────────────────────
  const handleRefresh = useCallback(async () => {
    try {
      if (adapter && typeof adapter.list === 'function') {
        setLoading(true);
        const next = await adapter.list();
        if (Array.isArray(next)) setRows(next);
      } else if (typeof initialRows === 'function') {
        // Demo / sandbox mode — re-run the seed.
        setRows(initialRows());
      }
      const [title, desc] = notifyCopy.refreshSuccess();
      notifySuccess(title, desc);
    } catch (err) {
      const [title, fallback] = notifyCopy.refreshError();
      notifyError(title, describeError(err, fallback));
    } finally {
      setLoading(false);
    }
  }, [adapter, initialRows, notifySuccess, notifyError, notifyCopy]);

  // ── Update single cell (inline edit) ─────────────────────────
  // Wired by the grid as `onUpdateCell={ctrl.gridHandlers.onUpdateCell}`.
  //
  // Resolution order (matches the inline-edit safety contract):
  //   1. `adapter.updateCell(id, field, value)` — preferred per-cell call
  //   2. `adapter.update(id, { [field]: value })` — full-row fallback
  //
  // The fourth parameter `_originalValue` carries the pre-edit value;
  // future audit-trail callers can wrap this handler and read it.
  // We intentionally keep it in the signature so the grid → controller
  // contract documents what's available.
  //
  // No success toast — cell-level edits are high-frequency and toast
  // spam is worse than a silent commit. Errors still toast so the
  // user knows what went wrong, and the inline editor stays in edit
  // mode (we re-throw) so the typed value isn't lost.
  const handleUpdateCell = useCallback(
    // eslint-disable-next-line no-unused-vars
    async (record, field, newValue, _originalValue) => {
      const id = getId(record);
      try {
        let next;
        if (adapter && typeof adapter.updateCell === 'function') {
          next = await adapter.updateCell(id, field, newValue);
        } else if (adapter && typeof adapter.update === 'function') {
          next = await adapter.update(id, { [field]: newValue }, record);
        }
        setRows((prev) =>
          prev.map((r) =>
            getId(r) === id
              ? {
                  ...(next && typeof next === 'object' ? { ...r, ...next } : r),
                  [field]: newValue
                }
              : r
          )
        );
      } catch (err) {
        const parsed = parseApiError(err);
        notifyError(
          `Could not update ${field}`,
          (parsed && parsed.description) || 'Please try again.'
        );
        throw err;
      }
    },
    [adapter, getId, notifyError]
  );

  // ── Submit (add / edit) ───────────────────────────────────────
  const handleSubmit = useCallback(
    async (mode, values, original) => {
      try {
        if (mode === 'add') {
          let next;
          if (adapter && typeof adapter.create === 'function') {
            next = await adapter.create(values);
          } else {
            next = { ...values, id: getId(values) || makeLocalId() };
          }
          setRows((prev) => [next, ...prev]);
          const [title, desc] = notifyCopy.addSuccess(next);
          notifySuccess(title, desc);
          return next;
        }

        if (mode === 'edit') {
          const id = getId(original);
          let next;
          if (adapter && typeof adapter.update === 'function') {
            next = await adapter.update(id, values, original);
          } else {
            next = { ...original, ...values };
          }
          setRows((prev) =>
            prev.map((r) => (getId(r) === id ? { ...r, ...next } : r))
          );
          const [title, desc] = notifyCopy.editSuccess(next);
          notifySuccess(title, desc);
          return next;
        }
        return null;
      } catch (err) {
        const copy =
          mode === 'add' ? notifyCopy.addError() : notifyCopy.editError();
        notifyError(copy[0], describeError(err, copy[1]));
        // Re-throw so the form modal stays open (ErpDataGridFormModal closes
        // only on a clean resolve).
        throw err;
      }
    },
    [adapter, getId, notifyCopy, notifySuccess, notifyError]
  );

  // ── Delete (single) ───────────────────────────────────────────
  const handleDelete = useCallback(
    async (record) => {
      const id = getId(record);
      const resourceLabel =
        (getResourceLabel && getResourceLabel(record)) ||
        (getDisplayName && getDisplayName(record)) ||
        String(id);

      try {
        if (approval && approval.delete) {
          await runWithApproval({
            operationKey: approval.delete.operationKey,
            resourceType: resourceType,
            resourceId: String(id),
            operationLabel:
              approval.delete.operationLabel ||
              `Delete ${entityLabel.singular.toLowerCase()}`,
            resourceLabel,
            metadata: approval.delete.metadata
              ? approval.delete.metadata(record)
              : { id },
            action: (challengeId) => approval.delete.action(challengeId, record)
          });
        } else if (adapter && typeof adapter.delete === 'function') {
          await adapter.delete(id, record);
        }
        setRows((prev) => prev.filter((r) => getId(r) !== id));
        const [title, desc] = notifyCopy.deleteSuccess(record);
        notifySuccess(title, desc);
      } catch (err) {
        if (err && err.name === 'ApprovalMfaCancelled') return;
        const [title, fallback] = notifyCopy.deleteError();
        notifyError(title, describeError(err, fallback));
      }
    },
    [
      approval,
      adapter,
      runWithApproval,
      resourceType,
      entityLabel,
      getId,
      getResourceLabel,
      getDisplayName,
      notifyCopy,
      notifySuccess,
      notifyError
    ]
  );

  // ── Bulk delete ───────────────────────────────────────────────
  const handleBulkDelete = useCallback(
    async (_keys, records) => {
      const ids = records.map(getId);
      const idSet = new Set(ids);

      try {
        if (approval && approval.bulkDelete) {
          const cfg = approval.bulkDelete;
          const resourceId = cfg.resourceId
            ? cfg.resourceId(records)
            : `BULK:${records.length}`;
          await runWithApproval({
            operationKey: cfg.operationKey,
            resourceType: resourceType,
            resourceId,
            operationLabel:
              cfg.operationLabel ||
              `Bulk delete (${records.length} ${pluralise(
                entityLabel.singular.toLowerCase(),
                records.length
              )})`,
            resourceLabel: `${records.length} record${
              records.length === 1 ? '' : 's'
            } selected`,
            metadata: cfg.metadata ? cfg.metadata(records) : { ids },
            action: (challengeId) => cfg.action(challengeId, records)
          });
        } else if (adapter && typeof adapter.bulkDelete === 'function') {
          await adapter.bulkDelete(ids, records);
        } else if (adapter && typeof adapter.delete === 'function') {
          // Fall back to per-record delete if no bulk endpoint exists.
          for (const record of records) {
            // eslint-disable-next-line no-await-in-loop
            await adapter.delete(getId(record), record);
          }
        }
        setRows((prev) => prev.filter((r) => !idSet.has(getId(r))));
        const [title, desc] = notifyCopy.bulkDeleteSuccess(records);
        notifySuccess(title, desc);
      } catch (err) {
        if (err && err.name === 'ApprovalMfaCancelled') return;
        const [title, fallback] = notifyCopy.bulkDeleteError();
        notifyError(title, describeError(err, fallback));
      }
    },
    [
      approval,
      adapter,
      runWithApproval,
      resourceType,
      entityLabel,
      getId,
      notifyCopy,
      notifySuccess,
      notifyError
    ]
  );

  // Wrap any `bulkActions[].handler` that declares `requiresApprovalMfa`
  // in the existing step-up flow. The wrapped handler runs the same
  // shape the bulk-action bar already expects: `(records, ids)`.
  //
  // SECURITY: this wrapper is a UX gate, not an authorisation gate.
  // The backend MUST still verify the approval-MFA challenge on every
  // protected endpoint — never trust the frontend alone. The shared
  // `useApprovalMfa` hook creates a server-side challenge that the
  // protected action must consume; do not bypass that flow.
  const wrappedBulkActions = useMemo(() => {
    if (!Array.isArray(bulkActionsInput) || bulkActionsInput.length === 0) {
      return undefined;
    }
    return bulkActionsInput.map((action) => {
      if (!action || typeof action.handler !== 'function') return action;
      const mfa = action.requiresApprovalMfa;
      if (!mfa || !mfa.operationKey) return action;
      return {
        ...action,
        handler: async (records, ids) => {
          try {
            await runWithApproval({
              operationKey: mfa.operationKey,
              resourceType: mfa.resourceType || resourceType,
              resourceId:
                typeof mfa.resourceId === 'function'
                  ? mfa.resourceId(records)
                  : mfa.resourceId || `BULK:${records.length}`,
              operationLabel:
                mfa.operationLabel ||
                action.label ||
                `Bulk ${action.key || 'action'}`,
              resourceLabel: `${records.length} record${
                records.length === 1 ? '' : 's'
              } selected`,
              metadata: { ids },
              action: () => action.handler(records, ids)
            });
          } catch (err) {
            if (err && err.name === 'ApprovalMfaCancelled') return;
            throw err;
          }
        }
      };
    });
  }, [bulkActionsInput, runWithApproval, resourceType]);

  // Inline-edit is only surfaced on the grid when EITHER the caller
  // wired `adapter.updateCell` (preferred) OR `adapter.update` (full-row
  // fallback). Without either, the grid stays read-only even for
  // columns marked `editable: true` — the spec requires this guard.
  const supportsInlineEdit = Boolean(
    adapter &&
      (typeof adapter.updateCell === 'function' ||
        typeof adapter.update === 'function')
  );

  const gridHandlers = useMemo(
    () => ({
      gridId,
      dataSource: rows,
      loading,
      onRefresh: handleRefresh,
      onSubmit: handleSubmit,
      onDelete: handleDelete,
      onBulkDelete: handleBulkDelete,
      onUpdateCell: supportsInlineEdit ? handleUpdateCell : undefined,
      bulkActions: wrappedBulkActions
    }),
    [
      gridId,
      rows,
      loading,
      handleRefresh,
      handleSubmit,
      handleDelete,
      handleBulkDelete,
      supportsInlineEdit,
      handleUpdateCell,
      wrappedBulkActions
    ]
  );

  return {
    rows,
    setRows,
    loading,
    setLoading,
    gridHandlers,
    ApprovalMfaModalSlot
  };
}
